import { useState, useEffect, useCallback } from 'react';
import { 
  Plus, GripVertical, ChevronRight, ChevronDown, Trash2, 
  Edit2, Save, X, ExternalLink, FileText, Link2, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/utils/apiClient';

export default function NavigationManager() {
  const [menuItems, setMenuItems] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [draggedItem, setDraggedItem] = useState(null);
  
  // New item form state
  const [newItemType, setNewItemType] = useState('page');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemUrl, setNewItemUrl] = useState('');
  const [newItemPage, setNewItemPage] = useState('');
  const [newItemParent, setNewItemParent] = useState('');

  // Fetch menu and pages
  const fetchData = useCallback(async () => {
    try {
      const [menuRes, pagesRes] = await Promise.all([
        api.get('/admin/cms/navigation'),
        api.get('/admin/cms/simple/pages')
      ]);
      
      setMenuItems(menuRes.data.items || []);
      setPages(pagesRes.data || []);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save menu
  const saveMenu = async () => {
    setSaving(true);
    try {
      await api.put('/admin/cms/navigation', { items: menuItems });
      toast.success('Navigation saved!');
    } catch (err) {
      toast.error('Failed to save navigation');
    }
    setSaving(false);
  };

  // Add menu item
  const addMenuItem = () => {
    let url = '';
    let label = newItemLabel;
    
    if (newItemType === 'page' && newItemPage) {
      const page = pages.find(p => p.slug === newItemPage);
      url = newItemPage === 'home' ? '/' : `/${newItemPage}`;
      label = label || page?.title || newItemPage;
    } else if (newItemType === 'custom') {
      url = newItemUrl;
    }
    
    if (!label) {
      toast.error('Please enter a label');
      return;
    }
    
    const newItem = {
      id: `menu-${Date.now()}`,
      label,
      url,
      type: newItemType,
      children: []
    };
    
    if (newItemParent) {
      // Add as child
      setMenuItems(items => addChildToParent(items, newItemParent, newItem));
    } else {
      // Add to root
      setMenuItems(items => [...items, newItem]);
    }
    
    setShowAddDialog(false);
    resetNewItemForm();
    toast.success('Menu item added');
  };

  const addChildToParent = (items, parentId, newItem) => {
    return items.map(item => {
      if (item.id === parentId) {
        return { ...item, children: [...(item.children || []), newItem] };
      }
      if (item.children?.length) {
        return { ...item, children: addChildToParent(item.children, parentId, newItem) };
      }
      return item;
    });
  };

  const resetNewItemForm = () => {
    setNewItemType('page');
    setNewItemLabel('');
    setNewItemUrl('');
    setNewItemPage('');
    setNewItemParent('');
  };

  // Delete menu item
  const deleteMenuItem = (itemId) => {
    setMenuItems(items => removeItem(items, itemId));
    toast.success('Menu item removed');
  };

  const removeItem = (items, itemId) => {
    return items.filter(item => item.id !== itemId).map(item => ({
      ...item,
      children: item.children ? removeItem(item.children, itemId) : []
    }));
  };

  // Update menu item
  const updateMenuItem = (itemId, updates) => {
    setMenuItems(items => updateItem(items, itemId, updates));
  };

  const updateItem = (items, itemId, updates) => {
    return items.map(item => {
      if (item.id === itemId) {
        return { ...item, ...updates };
      }
      if (item.children?.length) {
        return { ...item, children: updateItem(item.children, itemId, updates) };
      }
      return item;
    });
  };

  // Move item (reorder)
  const moveItem = (itemId, direction, parentId = null) => {
    setMenuItems(items => {
      if (parentId) {
        return items.map(item => {
          if (item.id === parentId && item.children) {
            return { ...item, children: reorderArray(item.children, itemId, direction) };
          }
          return item;
        });
      }
      return reorderArray(items, itemId, direction);
    });
  };

  const reorderArray = (arr, itemId, direction) => {
    const index = arr.findIndex(item => item.id === itemId);
    if (index === -1) return arr;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= arr.length) return arr;
    
    const newArr = [...arr];
    [newArr[index], newArr[newIndex]] = [newArr[newIndex], newArr[index]];
    return newArr;
  };

  // Toggle expand
  const toggleExpand = (itemId) => {
    setExpandedItems(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  // Get all menu items flattened (for parent selection)
  const getFlatMenuItems = (items, depth = 0) => {
    let flat = [];
    items.forEach(item => {
      flat.push({ ...item, depth });
      if (item.children?.length) {
        flat = [...flat, ...getFlatMenuItems(item.children, depth + 1)];
      }
    });
    return flat;
  };

  // Render menu item
  const renderMenuItem = (item, parentId = null, index = 0, total = 1) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems[item.id];
    const isEditing = editingItem === item.id;

    return (
      <div key={item.id} className="group">
        <div className={`flex items-center gap-2 p-3 bg-white dark:bg-gray-800 border rounded-lg mb-2 ${
          isEditing ? 'ring-2 ring-primary' : 'hover:shadow-sm'
        }`}>
          {/* Drag Handle */}
          <div className="cursor-grab text-gray-400 hover:text-gray-600">
            <GripVertical className="w-4 h-4" />
          </div>

          {/* Expand/Collapse */}
          {hasChildren ? (
            <button onClick={() => toggleExpand(item.id)} className="p-1">
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-gray-500" />
              ) : (
                <ChevronRight className="w-4 h-4 text-gray-500" />
              )}
            </button>
          ) : (
            <div className="w-6" />
          )}

          {/* Icon */}
          <div className="w-8 h-8 bg-primary/10 rounded flex items-center justify-center flex-shrink-0">
            {item.url === '/' ? (
              <Home className="w-4 h-4 text-primary" />
            ) : item.type === 'custom' ? (
              <Link2 className="w-4 h-4 text-primary" />
            ) : (
              <FileText className="w-4 h-4 text-primary" />
            )}
          </div>

          {/* Label & URL */}
          {isEditing ? (
            <div className="flex-1 flex items-center gap-2">
              <Input
                value={item.label}
                onChange={(e) => updateMenuItem(item.id, { label: e.target.value })}
                className="h-8"
                placeholder="Label"
              />
              <Input
                value={item.url}
                onChange={(e) => updateMenuItem(item.id, { url: e.target.value })}
                className="h-8"
                placeholder="URL"
              />
              <Button size="sm" variant="ghost" onClick={() => setEditingItem(null)}>
                <Save className="w-4 h-4" />
              </Button>
            </div>
          ) : (
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm">{item.label}</p>
              <p className="text-xs text-muted-foreground truncate">{item.url}</p>
            </div>
          )}

          {/* Actions */}
          {!isEditing && (
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {/* Move Up/Down */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveItem(item.id, 'up', parentId)}
                disabled={index === 0}
                className="h-7 w-7 p-0"
              >
                <span className="sr-only">Move up</span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 19V5M5 12l7-7 7 7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => moveItem(item.id, 'down', parentId)}
                disabled={index === total - 1}
                className="h-7 w-7 p-0"
              >
                <span className="sr-only">Move down</span>
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M12 5v14M5 12l7 7 7-7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingItem(item.id)}
                className="h-7 w-7 p-0"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setNewItemParent(item.id);
                  setShowAddDialog(true);
                }}
                className="h-7 w-7 p-0"
                title="Add sub-menu"
              >
                <Plus className="w-4 h-4" />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => deleteMenuItem(item.id)}
                className="h-7 w-7 p-0 text-red-500 hover:text-red-600"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
            {item.children.map((child, idx) => renderMenuItem(child, item.id, idx, item.children.length))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-muted-foreground">Loading navigation...</p>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b p-4 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Navigation</h1>
          <p className="text-sm text-muted-foreground">Manage your site navigation and menus</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { setNewItemParent(''); setShowAddDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" />
            Add Menu Item
          </Button>
          <Button onClick={saveMenu} disabled={saving}>
            <Save className="w-4 h-4 mr-1" />
            {saving ? 'Saving...' : 'Save Menu'}
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 max-w-3xl mx-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl border p-6">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <span>Main Navigation</span>
            <span className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
              {menuItems.length} items
            </span>
          </h3>

          {menuItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No menu items yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowAddDialog(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Add First Menu Item
              </Button>
            </div>
          ) : (
            <div>
              {menuItems.map((item, index) => renderMenuItem(item, null, index, menuItems.length))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
          <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">Tips</h4>
          <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
            <li>• Drag items to reorder them</li>
            <li>• Click the + button on any item to add a sub-menu</li>
            <li>• Use arrows to move items up/down quickly</li>
            <li>• Don't forget to click "Save Menu" to save your changes</li>
          </ul>
        </div>
      </div>

      {/* Add Menu Item Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {newItemParent ? 'Add Sub-Menu Item' : 'Add Menu Item'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Type Selection */}
            <div className="space-y-2">
              <Label>Link Type</Label>
              <Select value={newItemType} onValueChange={setNewItemType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="page">Page Link</SelectItem>
                  <SelectItem value="custom">Custom URL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Page Selection */}
            {newItemType === 'page' && (
              <div className="space-y-2">
                <Label>Select Page</Label>
                <Select value={newItemPage} onValueChange={setNewItemPage}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a page..." />
                  </SelectTrigger>
                  <SelectContent>
                    {pages.map(page => (
                      <SelectItem key={page.slug} value={page.slug}>
                        {page.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Custom URL */}
            {newItemType === 'custom' && (
              <div className="space-y-2">
                <Label>URL</Label>
                <Input
                  value={newItemUrl}
                  onChange={(e) => setNewItemUrl(e.target.value)}
                  placeholder="https://example.com or /page"
                />
              </div>
            )}

            {/* Label */}
            <div className="space-y-2">
              <Label>Menu Label</Label>
              <Input
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                placeholder="e.g., About Us"
              />
              <p className="text-xs text-muted-foreground">
                Leave blank to use page title
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDialog(false); resetNewItemForm(); }}>
              Cancel
            </Button>
            <Button onClick={addMenuItem}>
              Add to Menu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
